import { useInstallationDevices, useInstallationSubDevices } from 'src/Libs/flow/installation/hooks'
import { Device } from 'src/Libs/flow/installation/forms'
import { PairingState } from 'src/Libs/flow/installation/api/installations'
import { useQntmStorageList } from 'src/Libs/installation-details/hooks/use-qntm-storage-list'
import { useProductSerialnumbers } from './use-product-serialnumbers'
import { useEmcStatus } from './em360/use-emc-status'
import plannedPowerMeterDtoToModelTransformer from '../../api/transformer/planned-power-meter-dto-to-model.transformer'
import { PlannedPowerMeterDeviceType } from '../../models'

export type PlannedInstallationComponent = Pick<Device, 'id' | 'name' | 'deviceCategory' | 'serialNumber'> & {
  pairingState?: PairingState
  deviceType?: PlannedPowerMeterDeviceType
}

export const usePlannedInstallationDevices = (
  installationId: number,
  emcDeviceLinkId?: string
): { devices: PlannedInstallationComponent[]; isEmcOnline: boolean } => {
  const { data } = useInstallationDevices(installationId)
  const emc = data?.emc
  const emcId = emc?.id?.toString() ?? emcDeviceLinkId
  const [productSerialnumbers] = useProductSerialnumbers(emc?.deviceId ? [emc.deviceId] : undefined)
  const emcSerialNumber = productSerialnumbers[0]?.serialno?.toString()
  const { isOnline: isEmcOnline } = useEmcStatus(emcSerialNumber)
  const { data: storages } = useQntmStorageList(installationId.toString(), emcId ? Number.parseInt(emcId) : 0)
  const { data: subDevices } = useInstallationSubDevices(installationId, emcId ?? null, !!emcId)

  const devices: PlannedInstallationComponent[] = [
    ...(emc || emcId ? [{ id: `emc-${emcId}`, name: '', deviceCategory: 'EMC' as const, serialNumber: emcSerialNumber ?? '' }] : []),
    ...(storages ?? []).map(storage => ({
      id: `storage-${storage.id}`,
      name: '',
      deviceCategory: 'Storage' as const,
      serialNumber: storage.deviceName ?? '',
    })),
    ...(subDevices?.monitoring?.meters ?? []).flatMap(meterDto => {
      const meter = plannedPowerMeterDtoToModelTransformer(meterDto)
      return meter
        ? [
            {
              id: `meter-${meter.id}`,
              name: meter.deviceName,
              deviceCategory: 'Meter' as const,
              serialNumber: meter.mediaParameters?.serialNumber ?? '',
              pairingState: meterDto.pairingState ?? undefined,
              deviceType: meter.deviceType,
            },
          ]
        : []
    }),
  ]

  return { devices, isEmcOnline }
}
